import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Types, metaData } from '@cornerstonejs/core';
import type { Types as CornerstoneTypes } from '@cornerstonejs/core';
import {
  selectActiveMeasurement,
  selectMeasurementStatus,
} from '@/state/slices/measurementSession';
import { getCornerstoneToolName } from '@/utils/measurementToolMapping';
import { SPECTRAL_DOPPLER_TOOL_NAMES } from '@/models/spectralDoppler';
import { resolveSpectralRegion } from '@/features/study-detail/Viewer/tools/SpectralDoppler/calibrationRegion';
import { isTimeUnit } from '@/features/study-detail/Viewer/tools/SpectralDoppler/calibrationMath';
import type { UltrasoundRegionsMeta } from '@/features/study-detail/Viewer/tools/SpectralDoppler/SpectralDopplerBaseTool';

interface UseMeasurementToolActivationProps {
  viewport: CornerstoneTypes.IStackViewport | Types.IVideoViewport | null;
  viewportId: string;
  activeViewportId: string | null;
  activeTool: string | undefined;
  changeTool: (tool: string) => void;
  deactivateTool: () => void;
  pauseAll: () => void;
}

const SPECTRAL_TOOL_TYPES = new Set([
  'spectral-doppler-vti',
  'spectral-doppler-probe',
  'spectral-doppler-segment',
]);

function viewportShowsSpectralDoppler(
  viewport: CornerstoneTypes.IStackViewport | Types.IVideoViewport | null
): boolean {
  if (!viewport) return false;
  const imageId =
    (
      viewport as unknown as { getCurrentImageId?: () => string | undefined }
    ).getCurrentImageId?.() ?? '';
  if (!imageId) return false;
  const regionsMeta = metaData.get('ultrasoundRegions', imageId) as
    | UltrasoundRegionsMeta
    | undefined;
  return resolveSpectralRegion(regionsMeta?.regions).reason === 'ok';
}

export function useMeasurementToolActivation({
  viewport,
  viewportId,
  activeViewportId,
  activeTool,
  changeTool,
  deactivateTool,
  pauseAll,
}: UseMeasurementToolActivationProps): void {
  const activeMeasurement = useSelector(selectActiveMeasurement);
  const measurementStatus = useSelector(selectMeasurementStatus);

  // Bug fix (Bug 5): Track which measurement has already been activated
  const activatedMeasurementClassRef = useRef<string | null>(null);

  useEffect(() => {
    if (!activeMeasurement) {
      activatedMeasurementClassRef.current = null;
      return;
    }

    const isSpectralTool = SPECTRAL_TOOL_TYPES.has(activeMeasurement.toolType);

    if (isSpectralTool) {
      // Spectral tools auto-target the viewport showing spectral doppler so
      // clicking a VTI/probe/segment row in the Report activates the tool
      // immediately without requiring the user to hover the doppler pane.
      if (!viewportShowsSpectralDoppler(viewport)) return;
    } else if (activeViewportId !== viewportId) {
      // Non-spectral tools keep the hover gate to prevent cross-viewport
      // activation when multiple panes are open.
      return;
    }

    if (measurementStatus !== 'awaiting' || !viewport) return;

    // Skip only when the *target* tool is already active. A blanket
    // `if (activeTool) return` blocks legitimate tool switches (e.g. clicking
    // a VTI row in the Report while Length is active should swap to VTI).
    // A Slope row measured in ms only records a time: draw it with Time.
    const toolName =
      activeMeasurement.toolType === 'spectral-doppler-segment' &&
      isTimeUnit(activeMeasurement.unit)
        ? SPECTRAL_DOPPLER_TOOL_NAMES.TIME
        : getCornerstoneToolName(activeMeasurement.toolType);
    if (activeTool === toolName) return;

    activatedMeasurementClassRef.current =
      activeMeasurement.canonicalMeasurement;
    pauseAll();
    changeTool(toolName);
  }, [
    activeMeasurement,
    measurementStatus,
    viewport,
    activeTool,
    changeTool,
    pauseAll,
    activeViewportId,
    viewportId,
  ]);

  // Bug fix (Bug 4): Deactivate tool as soon as measurement leaves 'awaiting'
  const prevMeasurementStatusRef = useRef(measurementStatus);

  useEffect(() => {
    const prevStatus = prevMeasurementStatusRef.current;
    prevMeasurementStatusRef.current = measurementStatus;

    if (prevStatus === 'awaiting' && measurementStatus !== 'awaiting') {
      deactivateTool();
    }
  }, [measurementStatus, deactivateTool]);
}
